import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Lock, Sparkles } from "lucide-react";
import { Logo } from "../Logo";
import { useSports } from "../../hooks/useSports";
import { ICON_MAP } from "../../data/sports";

const Nav_LINKS = [
  { label: "Home", to: "/" },
  { label: "Standings", to: "/standings" },
  { label: "World Cup 2026", to: "/standings" },
];

export const Navbar = () => {
  const sports = useSports();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const SportItem = ({ sport, onClick }) => {
    const Icon = ICON_MAP[sport.icon] || ICON_MAP.Trophy;
    const isActive = sport.status === "active";
    return (
      <Link
        to={`/sports/${sport.slug}`}
        onClick={onClick}
        data-testid={`nav-sport-${sport.slug}`}
        className={`group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-300 ${
          isActive
            ? "text-white hover:bg-brand-navy/60"
            : "text-brand-silver hover:bg-brand-navy/40 hover:text-white"
        }`}
      >
        <span className="flex items-center gap-2.5">
          <Icon className={`h-4 w-4 ${isActive ? "text-brand-cyan" : "text-brand-silver group-hover:text-brand-cyan"}`} />
          {sport.name}
        </span>
        {isActive ? (
          <span className="font-mono-data text-[9px] font-semibold uppercase tracking-wider text-brand-cyan">
            Live
          </span>
        ) : (
          <Lock className="h-3 w-3 text-brand-silver/60" />
        )}
      </Link>
    );
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b border-brand-navy/80 bg-brand-bg/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {Nav_LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive && l.to === "/" && location.pathname === "/"
                    ? "text-white"
                    : "text-brand-silver hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          {/* Sports dropdown */}
          <div className="group relative">
            <button
              data-testid="nav-sports-trigger"
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-brand-silver transition-colors duration-300 hover:text-white"
            >
              Sports
              <span className="font-mono-data text-[9px] text-brand-cyan">7</span>
            </button>
            <div className="invisible absolute right-0 top-full w-64 translate-y-2 pt-2 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              <div className="rounded-xl border border-brand-navy bg-brand-bg/95 p-2 shadow-2xl backdrop-blur-xl">
                {sports.map((s) => (
                  <SportItem key={s.slug} sport={s} />
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/sports/football"
            data-testid="nav-cta"
            className="ml-2 inline-flex items-center gap-2 rounded-lg bg-brand-royal px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-cyan hover:text-brand-bg"
          >
            <Sparkles className="h-4 w-4" />
            Free Analysis
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-brand-navy/80 bg-brand-bg/95 px-4 py-4 backdrop-blur-xl lg:hidden"
        >
          {Nav_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-2.5 text-sm font-medium text-white hover:bg-brand-navy/40"
            >
              {l.label}
            </Link>
          ))}
          <div className="my-2 h-px bg-brand-navy" />
          <p className="px-3 py-1 font-mono-data text-[10px] uppercase tracking-[0.2em] text-brand-silver">
            Sports
          </p>
          {sports.map((s) => (
            <SportItem key={s.slug} sport={s} onClick={() => setOpen(false)} />
          ))}
        </div>
      )}
    </header>
  );
};

export default Navbar;
